import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import driverMessagingService from '../../services/driverMessagingService';

// Async thunks
export const sendMessageToDriver = createAsyncThunk(
  'driverMessages/sendMessageToDriver',
  async ({ recipientId, title, message, priority = 'normal', expiresAt = null }, { rejectWithValue }) => {
    try {
      const messageId = await driverMessagingService.sendMessageToDriver(
        recipientId, 
        title, 
        message, 
        priority, 
        expiresAt
      );
      
      return {
        id: messageId,
        recipientId,
        title,
        message,
        priority,
        type: 'central_message',
        status: 'active',
        createdAt: new Date().toISOString(),
        readBy: [],
        ...(expiresAt && { expiresAt: expiresAt.toISOString() })
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendBroadcastMessage = createAsyncThunk(
  'driverMessages/sendBroadcastMessage',
  async ({ title, message, priority = 'normal', expiresAt = null }, { rejectWithValue }) => {
    try {
      const messageId = await driverMessagingService.sendBroadcastMessage(
        title, 
        message, 
        priority, 
        expiresAt
      );
      
      return {
        id: messageId,
        recipientId: 'all',
        title,
        message,
        priority,
        type: 'central_message',
        status: 'active',
        createdAt: new Date().toISOString(),
        readBy: [],
        ...(expiresAt && { expiresAt: expiresAt.toISOString() })
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendMultipleMessages = createAsyncThunk(
  'driverMessages/sendMultipleMessages',
  async ({ recipientId, messages, delayMs = 1000 }, { rejectWithValue }) => {
    try {
      const messageIds = await driverMessagingService.sendMultipleMessages(
        recipientId, 
        messages, 
        delayMs
      );
      
      return messageIds.map((id, index) => ({
        id,
        recipientId,
        title: messages[index].title,
        message: messages[index].message,
        priority: 'normal',
        type: 'central_message',
        status: 'active',
        createdAt: new Date().toISOString(),
        readBy: []
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSentMessages = createAsyncThunk(
  'driverMessages/fetchSentMessages',
  async ({ limitCount = 50 } = {}, { rejectWithValue }) => {
    try {
      const messages = await driverMessagingService.fetchSentMessages(limitCount);
      return messages;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMessagesForDriver = createAsyncThunk(
  'driverMessages/fetchMessagesForDriver',
  async ({ driverId }, { rejectWithValue }) => {
    try {
      const messages = await driverMessagingService.fetchMessagesForDriver(driverId);
      return messages;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTestMessage = createAsyncThunk(
  'driverMessages/createTestMessage',
  async ({ recipientId = 'all' } = {}, { rejectWithValue }) => {
    try {
      const messageId = await driverMessagingService.createTestMessage(recipientId);
      return {
        id: messageId,
        recipientId,
        title: 'Test-Nachricht',
        message: 'Dies ist eine Test-Nachricht vom Admin-Panel.',
        priority: 'normal',
        type: 'central_message',
        status: 'active',
        createdAt: new Date().toISOString(),
        readBy: []
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createMultipleTestMessages = createAsyncThunk(
  'driverMessages/createMultipleTestMessages',
  async ({ recipientId = 'all' } = {}, { rejectWithValue }) => {
    try {
      const messageIds = await driverMessagingService.createMultipleTestMessages(recipientId);
      
      const testMessages = [
        { title: 'Nachricht 1', message: 'Erste Test-Nachricht. Diese wird 3 Sekunden angezeigt.' },
        { title: 'Nachricht 2', message: 'Zweite Test-Nachricht. Diese wird nach der ersten angezeigt.' },
        { title: 'Nachricht 3', message: 'Dritte Test-Nachricht. Alle Nachrichten landen im Glocken-Button.' }
      ];
      
      return messageIds.map((id, index) => ({
        id,
        recipientId,
        title: testMessages[index].title,
        message: testMessages[index].message,
        priority: 'normal',
        type: 'central_message',
        status: 'active',
        createdAt: new Date().toISOString(),
        readBy: []
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  sentMessages: [],
  driverMessages: [],
  loading: false,
  error: null,
  sendingMessage: false,
  lastSentMessage: null,
  messageHistory: []
};

const driverMessagesSlice = createSlice({
  name: 'driverMessages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLastSentMessage: (state) => {
      state.lastSentMessage = null;
    },
    addToMessageHistory: (state, action) => {
      state.messageHistory.unshift(action.payload);
      // Behalte nur die letzten 20 Nachrichten im Verlauf
      if (state.messageHistory.length > 20) {
        state.messageHistory = state.messageHistory.slice(0, 20);
      }
    },
    clearMessageHistory: (state) => {
      state.messageHistory = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Send Message to Driver
      .addCase(sendMessageToDriver.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendMessageToDriver.fulfilled, (state, action) => {
        state.sendingMessage = false;
        state.lastSentMessage = action.payload;
        state.sentMessages.unshift(action.payload);
        state.messageHistory.unshift({
          ...action.payload,
          action: 'sent_individual',
          timestamp: new Date().toISOString()
        });
      })
      .addCase(sendMessageToDriver.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })

      // Send Broadcast Message
      .addCase(sendBroadcastMessage.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendBroadcastMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        state.lastSentMessage = action.payload;
        state.sentMessages.unshift(action.payload);
        state.messageHistory.unshift({
          ...action.payload,
          action: 'sent_broadcast',
          timestamp: new Date().toISOString()
        });
      })
      .addCase(sendBroadcastMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })

      // Send Multiple Messages
      .addCase(sendMultipleMessages.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendMultipleMessages.fulfilled, (state, action) => {
        state.sendingMessage = false;
        state.sentMessages.unshift(...action.payload);
        action.payload.forEach(message => {
          state.messageHistory.unshift({
            ...message,
            action: 'sent_multiple',
            timestamp: new Date().toISOString()
          });
        });
      })
      .addCase(sendMultipleMessages.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })

      // Fetch Sent Messages
      .addCase(fetchSentMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSentMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.sentMessages = action.payload;
      })
      .addCase(fetchSentMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Messages for Driver
      .addCase(fetchMessagesForDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessagesForDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.driverMessages = action.payload;
      })
      .addCase(fetchMessagesForDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Test Message
      .addCase(createTestMessage.fulfilled, (state, action) => {
        state.lastSentMessage = action.payload;
        state.sentMessages.unshift(action.payload);
        state.messageHistory.unshift({
          ...action.payload,
          action: 'sent_test',
          timestamp: new Date().toISOString()
        });
      })

      // Create Multiple Test Messages
      .addCase(createMultipleTestMessages.fulfilled, (state, action) => {
        state.sentMessages.unshift(...action.payload);
        action.payload.forEach(message => {
          state.messageHistory.unshift({
            ...message,
            action: 'sent_test_multiple',
            timestamp: new Date().toISOString()
          });
        });
      });
  }
});

export const { 
  clearError, 
  clearLastSentMessage, 
  addToMessageHistory, 
  clearMessageHistory 
} = driverMessagesSlice.actions;

export default driverMessagesSlice.reducer;